import React from 'react'
import { useDispatch } from 'react-redux'
import { AppDispatch } from '@/store'
import { applySuggestion } from '@/store/slices/ragChatSlice'
import { ChatSuggestion } from '@/store/slices/ragChatSlice'
import { 
  SparklesIcon,
  ClockIcon,
  StarIcon,
  LightBulbIcon
} from '@heroicons/react/24/outline'

interface ChatSuggestionsProps {
  suggestions: ChatSuggestion[]
  compact?: boolean
  className?: string
}

export const ChatSuggestions: React.FC<ChatSuggestionsProps> = ({ 
  suggestions, 
  compact = false,
  className = ''
}) => {
  const dispatch = useDispatch<AppDispatch>()

  // 處理建議點擊
  const handleSuggestionClick = (suggestion: ChatSuggestion) => {
    dispatch(applySuggestion(suggestion.text))
    // 可以選擇立即發送或只是填入輸入框
    // dispatch(sendMessage({ message: suggestion.text }))
  }

  // 獲取建議的圖標
  const getSuggestionIcon = (category: ChatSuggestion['category'], icon?: string) => {
    if (icon) {
      // 如果有自定義 emoji 圖標，直接使用
      return <span className="text-sm">{icon}</span>
    }

    // 根據類別返回相應圖標
    switch (category) {
      case 'recent':
        return <ClockIcon className="h-3 w-3" />
      case 'context':
        return <SparklesIcon className="h-3 w-3" />
      case 'common':
        return <StarIcon className="h-3 w-3" />
      default:
        return <LightBulbIcon className="h-3 w-3" />
    }
  }

  // 獲取建議的樣式類別
  const getSuggestionClass = (category: ChatSuggestion['category']) => {
    const baseClass = `
      flex items-center space-x-2 px-3 py-2 rounded-lg border cursor-pointer
      transition-all duration-200 hover:shadow-sm text-left
      ${compact ? 'text-xs' : 'text-sm'}
    `

    switch (category) {
      case 'recent':
        return `${baseClass} bg-orange-50 border-orange-200 text-orange-700 hover:bg-orange-100`
      case 'context':
        return `${baseClass} bg-blue-50 border-blue-200 text-blue-700 hover:bg-blue-100`
      case 'common':
        return `${baseClass} bg-green-50 border-green-200 text-green-700 hover:bg-green-100`
      default:
        return `${baseClass} bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100`
    }
  }

  if (suggestions.length === 0) {
    return null
  }

  return (
    <div className={`space-y-2 ${className}`}>
      {!compact && (
        <div className="text-xs text-gray-500 flex items-center space-x-1">
          <LightBulbIcon className="h-3 w-3" />
          <span>建議查詢:</span>
        </div>
      )}
      
      <div className={`
        ${compact 
          ? 'flex flex-wrap gap-2' 
          : 'grid grid-cols-1 gap-2'
        }
      `}>
        {suggestions.map((suggestion) => (
          <button
            key={suggestion.id}
            onClick={() => handleSuggestionClick(suggestion)}
            className={getSuggestionClass(suggestion.category)}
          >
            <div className="flex-shrink-0">
              {getSuggestionIcon(suggestion.category, suggestion.icon)}
            </div>
            
            <span className="flex-1 truncate">
              {suggestion.text}
            </span>
            
            {/* 類別標籤 */}
            {!compact && (
              <span className={`
                text-xs px-1.5 py-0.5 rounded opacity-75
                ${suggestion.category === 'recent' ? 'bg-orange-200 text-orange-600' :
                  suggestion.category === 'context' ? 'bg-blue-200 text-blue-600' :
                  'bg-green-200 text-green-600'
                }
              `}>
                {suggestion.category === 'recent' ? '最近' :
                 suggestion.category === 'context' ? '上下文' : '常用'
                }
              </span>
            )}
          </button>
        ))}
      </div>
      
      {/* 提示文字 */}
      {!compact && (
        <div className="text-xs text-gray-400 text-center pt-2">
          點擊建議快速開始對話，或直接輸入您的問題
        </div>
      )}
    </div>
  )
}

export default ChatSuggestions
